<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { UiForm, UiFormField, UiTextarea, UiText, UiChip, UiTooltip, UiStandardButton } from "@shopexperts/shared";
import type { ExpertAiContext } from "@shopexperts/legacy-api";
import {
  AI_CONTEXT_FIELDS,
  AI_CONTEXT_TRUST_DESCRIPTIONS,
  AI_CONTEXT_TRUST_LABELS,
  aiContextChanges,
  aiContextFormSchema,
  aiContextToFormValues,
  type AiContextFormValues,
  type AiContextTrust,
} from "@/app/composables/expert-manager/aiContextForm";
import { formatAiDateTime } from "@/app/composables/expert-manager/aiSummaries";

const props = defineProps<{
  context: ExpertAiContext | null;
  isSaving?: boolean;
}>();

const emit = defineEmits<{
  (e: "save", changes: ReturnType<typeof aiContextChanges>): void;
}>();

const saved = computed(() => aiContextToFormValues(props.context));
const model = ref<AiContextFormValues>({ ...saved.value });
const formKey = ref(0);

const changes = computed(() => aiContextChanges(saved.value, model.value));
const isDirty = computed(() => Object.keys(changes.value).length > 0);

// A background refetch must not wipe unsaved edits; Discard re-seeds from the latest server values.
watch(saved, (next) => {
  if (isDirty.value) return;
  model.value = { ...next };
  formKey.value += 1;
});

function trustVariant(trust: AiContextTrust): "neutral" | "highlight" | "dark" {
  if (trust === "authoritative") return "dark";
  if (trust === "verified") return "highlight";
  return "neutral";
}

function discard() {
  model.value = { ...saved.value };
  formKey.value += 1;
}

function submit(values: AiContextFormValues) {
  emit("save", aiContextChanges(saved.value, values));
}

const updatedLine = computed(() => {
  if (!props.context?.updated_at) return "Not set yet. Saved fields feed every future generation for this expert.";
  const by = props.context.updated_by?.name ?? props.context.updated_by?.email;
  return `Last saved ${formatAiDateTime(props.context.updated_at)}${by ? ` by ${by}` : ""}.`;
});
</script>

<template>
  <UiForm
    :key="formKey"
    v-model="model"
    :schema="aiContextFormSchema"
    :initial-values="saved"
    @submit="submit"
  >
    <div class="flex flex-col gap-5">
      <UiText as="p" variant="caption-normal" class="text-se-gray-500" :text="updatedLine" />

      <div class="grid gap-5 lg:grid-cols-2">
        <UiFormField v-for="field in AI_CONTEXT_FIELDS" :key="field.name" :name="field.name">
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
            <div class="flex flex-col gap-1.5">
              <div class="flex items-center gap-2">
                <UiText as="label" variant="label" :for="id" :text="field.label" />
                <UiTooltip :content="AI_CONTEXT_TRUST_DESCRIPTIONS[field.trust]">
                  <UiChip :text="AI_CONTEXT_TRUST_LABELS[field.trust]" :variant="trustVariant(field.trust)" size="xs" rounded="full" />
                </UiTooltip>
              </div>
              <UiTextarea
                :id="id"
                :name="field.name"
                :model-value="(modelValue as string) ?? ''"
                :rows="4"
                :placeholder="field.placeholder"
                :hint="field.hint"
                :invalid="invalid"
                :error="error"
                :disabled="isSaving"
                @update:model-value="onUpdateModelValue"
                @validate="onValidate"
              />
            </div>
          </template>
        </UiFormField>
      </div>

      <div class="flex items-center justify-end gap-2">
        <UiStandardButton variant="ghost" size="sm" label="Discard" :disabled="!isDirty || isSaving" @click="discard" />
        <UiStandardButton type="submit" variant="primary" size="sm" :label="isSaving ? 'Saving…' : 'Save context'" :disabled="!isDirty || isSaving" />
      </div>
    </div>
  </UiForm>
</template>
